import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useMobileOptimizedAnimation } from '../hooks/useMobileOptimizedAnimation';

const ValueProposition = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { getInViewVariants, getStaggeredTransition, getHoverVariants } = useMobileOptimizedAnimation();

  const features = [
    {
      icon: (
        <svg className="w-10 h-10 xs:w-12 xs:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      title: 'AI-Driven Engineering Insights',
      description: 'Visualize loads, strain, and cracks with intelligent real-time data processing and predictive analytics.',
      metrics: '99.7% accuracy',
      color: 'from-blue-500 to-primary-600'
    },
    {
      icon: (
        <svg className="w-10 h-10 xs:w-12 xs:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      title: 'Sensor Agnostic Platform',
      description: 'Seamlessly integrates with 67+ sensor types—wireless, tilt, corrosion, strain, and environmental sensors.',
      metrics: '67+ sensor types',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: (
        <svg className="w-10 h-10 xs:w-12 xs:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: 'Actionable Dashboards & Reports',
      description: 'Transform complex data into stakeholder-ready insights with intuitive visualizations and automated reporting.',
      metrics: 'Real-time updates',
      color: 'from-purple-500 to-indigo-600'
    },
  ];

  return (
    <section id="features" className="section-padding-mobile bg-gradient-to-b from-gray-25 to-white relative">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-12 -right-12 w-48 h-48 xs:-top-24 xs:-right-24 xs:w-96 xs:h-96 bg-primary-100/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-12 -left-12 w-48 h-48 xs:-bottom-24 xs:-left-24 xs:w-96 xs:h-96 bg-accent-100/30 rounded-full blur-3xl"></div>
      </div>

      <div className="container-mobile relative">
        <motion.div
          ref={ref}
          {...getInViewVariants()}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          className="text-center mb-12 xs:mb-16 lg:mb-20"
        >
          
          <h2 id="features-heading" className="text-mobile-title font-bold text-gray-900 mb-4 xs:mb-6">
            Our Value{' '}
            <span className="gradient-text-accent">Proposition</span>
          </h2>
          
          <p className="text-mobile-body text-gray-600 max-w-4xl mx-auto">
            Transforming infrastructure monitoring with cutting-edge technology and intelligent insights that drive better decisions
          </p>
        </motion.div>

        <div className="grid-mobile-3 gap-6 xs:gap-8 lg:gap-12">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={getStaggeredTransition(index)}
              {...getHoverVariants()}
              className="group relative"
            >
              {/* Gradient background card */}
              <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-2xl xs:rounded-3xl blur-xl" 
                   style={{ background: `linear-gradient(135deg, var(--tw-gradient-stops))` }}></div>
              
              <div className="relative bg-white rounded-2xl xs:rounded-3xl p-6 xs:p-8 lg:p-10 shadow-mobile-soft xs:shadow-soft hover:shadow-mobile-large xs:hover:shadow-large transition-mobile-smooth border border-gray-100 hover:border-gray-200 h-full">
                {/* Icon container */}
                <div className="relative mb-6 xs:mb-8">
                  <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} opacity-10 rounded-xl xs:rounded-2xl blur-xl`}></div>
                  <div className={`relative inline-flex items-center justify-center w-14 h-14 xs:w-16 xs:h-16 bg-gradient-to-r ${feature.color} rounded-xl xs:rounded-2xl text-white mb-2 transition-mobile-smooth shadow-mobile-medium xs:shadow-medium`}>
                    {feature.icon}
                  </div>
                  <div className="inline-flex items-center gap-2 bg-gray-50 rounded-full px-3 py-1 mt-3 xs:mt-4">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium text-gray-600">{feature.metrics}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-3 xs:space-y-4">
                  <h3 className="text-lg xs:text-xl lg:text-2xl font-semibold text-gray-900 leading-tight group-hover:text-primary-700 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm xs:text-base lg:text-lg">
                    {feature.description}
                  </p>
                </div>

              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA section */}
        <motion.div
          {...getInViewVariants()}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={getStaggeredTransition(4)} 
          className="text-center mt-12 xs:mt-16 lg:mt-20"
        >
          <div className="bg-white rounded-2xl xs:rounded-3xl p-6 xs:p-8 lg:p-12 shadow-mobile-medium xs:shadow-medium border border-gray-100 max-w-4xl mx-auto">
            <h3 className="text-xl xs:text-2xl lg:text-3xl font-semibold text-gray-900 mb-3 xs:mb-4">
              Ready to revolutionize your infrastructure monitoring?
            </h3>
            <p className="text-base xs:text-lg text-gray-600 mb-6 xs:mb-8">
              Join leading engineering teams who trust CIVIONIKS for mission-critical infrastructure insights.
            </p>
            <div className="flex justify-center">
              <button 
                onClick={() => {
                  const element = document.getElementById('api-access');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
                className="btn-primary-mobile group min-h-touch-md"
              >
                Request API Access
                <svg className="w-4 h-4 xs:w-5 xs:h-5 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Section divider */}
      <div className="section-divider mt-16 xs:mt-20 lg:mt-24"></div>
    </section>
  );
};

export default ValueProposition; 